import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Edit3, Plus, Trash2 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, ConfirmDialog, toast } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { canCreateLists, canDeleteLists, canEditLists } from '../../access';
import { useListsMutations, useRewriteListsQuery, useSchainListsQuery } from '../../hooks';
import {
  listsPageSizes,
  rewriteRuleSetCount,
  SupplyListsTab,
  type RewriteList,
  type SchainList,
} from '../../model';
import { useListsRouteFilters } from '../../useListsRouteFilters';

interface CollectionRow {
  id: string;
  name: string;
  items: number | null;
}

export function ListsCollectionPage({
  tab,
}: {
  tab: SupplyListsTab.Rewrite | SupplyListsTab.Schain;
}) {
  const { can } = useShigoPermissions();
  const canCreate = canCreateLists(can);
  const canEdit = canEditLists(can);
  const canDelete = canDeleteLists(can);
  const routeFilters = useListsRouteFilters(tab);
  const search = useDebouncedValue(
    routeFilters.filter.searchTerm,
    (value) => {
      routeFilters.setSearch(value);
    },
    650,
  );
  const rewriteQuery = useRewriteListsQuery(routeFilters.filter, tab === SupplyListsTab.Rewrite);
  const schainQuery = useSchainListsQuery(routeFilters.filter, tab === SupplyListsTab.Schain);
  const query = tab === SupplyListsTab.Rewrite ? rewriteQuery : schainQuery;
  const mutations = useListsMutations();
  const [deleting, setDeleting] = React.useState<CollectionRow | null>(null);

  const items =
    tab === SupplyListsTab.Rewrite
      ? (rewriteQuery.data?.content ?? []).map(toRewriteCollectionRow)
      : (schainQuery.data?.content ?? []).map(toSchainCollectionRow);
  const totalItems = query.data?.totalElements ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalItems / routeFilters.filter.size));
  const safePage = Math.min(routeFilters.filter.page, pageCount - 1);
  React.useEffect(() => {
    if (!query.isLoading && safePage !== routeFilters.filter.page) {
      routeFilters.setPagination(safePage, routeFilters.filter.size);
    }
  }, [query.isLoading, routeFilters, safePage]);

  async function deleteItem() {
    if (!deleting) return;
    try {
      if (tab === SupplyListsTab.Rewrite) await mutations.deleteRewrite.mutateAsync(deleting.id);
      else await mutations.deleteSchain.mutateAsync(deleting.id);
      toast.success(dashboardT('lists.deleted'));
      setDeleting(null);
    } catch (error) {
      showRequestError(error, dashboardT('lists.deleteError'));
    }
  }

  const columns = buildDashboardDataTableColumns<CollectionRow>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('lists.name'),
      headerClassName: 'w-[65%]',
      className: 'w-[65%] font-medium',
      value: (item) =>
        canEdit ? (
          <Link
            className={dashboardTableTextActionClassName()}
            to={`/supply/lists/${tab}/edit/$listId`}
            params={{ listId: item.id }}
          >
            {item.name}
          </Link>
        ) : (
          item.name
        ),
    },
    {
      id: 'items',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('lists.items'),
      headerClassName: 'w-32',
      className: 'w-32',
      value: (item) => item.items ?? '-',
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !canEdit && !canDelete,
      cell: (item) => (
        <DashboardTableRowActions label={dashboardT('lists.actions')}>
          {canEdit ? (
            <DashboardTableRowActionItem asChild>
              <Link to={`/supply/lists/${tab}/edit/$listId`} params={{ listId: item.id }}>
                <Edit3 className="h-4 w-4" aria-hidden="true" />
                {dashboardT('lists.edit')}
              </Link>
            </DashboardTableRowActionItem>
          ) : null}
          {canDelete ? (
            <DashboardTableRowActionItem
              onSelect={() => {
                setDeleting(item);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {dashboardT('lists.delete')}
            </DashboardTableRowActionItem>
          ) : null}
        </DashboardTableRowActions>
      ),
    },
  ]);

  const label =
    tab === SupplyListsTab.Rewrite
      ? dashboardT('lists.rewriteTitle')
      : dashboardT('lists.schainTitle');
  return (
    <main className="bg-background page-gutter min-h-0 flex-1 overflow-x-hidden py-4 md:py-6">
      <DashboardDataTable
        title={label}
        columns={columns}
        items={items}
        getRowKey={(item) => item.id}
        loading={query.isLoading}
        errorMessage={query.isError ? dashboardT('lists.loadError') : undefined}
        emptyMessage={dashboardT('lists.empty')}
        loadingMessage={dashboardT('lists.loading')}
        minWidthClassName="min-w-[640px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('lists.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
              placeholder={dashboardT('lists.search')}
            />
            {canCreate ? (
              <Button asChild>
                <Link to={`/supply/lists/${tab}/create`}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {dashboardT('lists.new')}
                </Link>
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: safePage,
            pageSize: routeFilters.filter.size,
            totalItems,
          },
          pageSizes: listsPageSizes,
          disabled: query.isLoading,
          onChange: ({ page, pageSize }) => {
            routeFilters.setPagination(page, pageSize);
          },
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={dashboardT('lists.deleteTitle')}
        description={dashboardT('lists.deleteDescription', { name: deleting?.name ?? '' })}
        cancelLabel={dashboardT('lists.cancel')}
        confirmLabel={dashboardT('lists.delete')}
        pending={mutations.deleteRewrite.isPending || mutations.deleteSchain.isPending}
        destructive
        onConfirm={() => {
          void deleteItem();
        }}
      />
    </main>
  );
}

function toRewriteCollectionRow(item: RewriteList): CollectionRow {
  return { id: item.id, name: item.name, items: rewriteRuleSetCount(item) };
}

function toSchainCollectionRow(item: SchainList): CollectionRow {
  return { id: item.id, name: item.name, items: item.schainBundleList.length };
}
